import React, { useEffect } from "react";
import "../../../assets/css/reservation.css";
import Hero from "../../structures/Hero";
import { usePageMeta } from "../../../hooks/usePageMeta";
import { useNavigate } from "react-router-dom";
import { useExperiences } from "../../../hooks/useExperiences";
import Footer from "../../structures/Footer";

const Reservation = () => {
  usePageMeta({
    title: "À L'AISE Reservation",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const { experiences, loading, getExp } = useExperiences();

  const handleBook = (experience) => {
    // Simpan ke localStorage sebagai cadangan jika state hilang (terutama di mobile)
    localStorage.setItem("selectedExperience", JSON.stringify(experience));
    navigate("/booking", { state: experience });
  };

  const checkAvailability = (exp) => {
    if (!exp) return { disabled: true, text: "Sedang Tidak Tersedia" };
    if (!exp.isActive) return { disabled: true, text: "Sedang Tidak Tersedia" };

    return { disabled: false, text: `Pesan ${exp.name}` };
  };

  const renderDynamicContent = (expId, fallbackDesc, fallbackIncludes = [], fallbackPrice = 200000, fallbackDuration = null) => {
    const exp = getExp(expId);
    const description = exp?.description || fallbackDesc;
    const includes = exp?.includes?.length > 0 ? exp.includes : fallbackIncludes;
    const price = exp?.price || fallbackPrice;

    return (
      <>
        <div className="experience-meta">
          <div className="meta-item">
            <span className="meta-label">HARGA</span>
            <span className="meta-value">
              {fmtPrice(price)} / {expId === 'kayak' ? 'perahu' : 'orang'}
            </span>
          </div>
          {(exp?.duration || true) && (
            <>
              <div className="meta-divider"></div>
              <div className="meta-item">
                <span className="meta-label">DURASI</span>
                <span className="meta-value">{exp?.duration || fallbackDuration || (expId.includes('combo') || expId.includes('painting-earthenware') ? '3 JAM' : '2 JAM')}</span>
              </div>
            </>
          )}
          {exp?.metaFields?.map((mf, i) => (
            <React.Fragment key={i}>
              <div className="meta-divider"></div>
              <div className="meta-item">
                <span className="meta-label">{mf.label}</span>
                <span className="meta-value">{mf.value}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <p className="experience-description">
          {description}
        </p>

        {includes?.length > 0 && (
          <div className="experience-includes">
            <h4 className="includes-title">PAKET TERMASUK</h4>
            <div className="includes-grid">
              {includes.map((item, idx) => (
                <div className="include-item" key={idx}>
                  <span className="include-icon">✓</span>
                  <span className="include-text">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pottery & Painting Specific Notes */}
        {(expId.includes('pottery') || expId.includes('painting') || expId.includes('combo')) && (
          <div className="experience-pottery-notes">
            <h5 className="notes-title">Catatan Kelas Pottery & Painting</h5>
            <ol className="notes-list">
                <li className="notes-item">
                <span className="notes-num">01</span>
                <p className="notes-text">Mohon datang tepat waktu sesuai jadwal booking karena terdapat sesi booking berikutnya. Keterlambatan lebih dari 10 menit akan menyebabkan booking hangus.</p>
              </li>
              <li className="notes-item">
                <span className="notes-num">02</span>
                <p className="notes-text">Booking dianggap terkonfirmasi setelah melakukan pembayaran.</p>
              </li>
              <li className="notes-item">
                <span className="notes-num">03</span>
                <p className="notes-text">Pembayaran yang sudah dilakukan tidak dapat di-refund. Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.</p>
              </li>
              <li className="notes-item">
                <span className="notes-num">04</span>
                <p className="notes-text">Hasil pottery dapat diambil setelah mendapatkan konfirmasi dari kami bahwa karya sudah selesai. Untuk painting, hasil karya dapat langsung dibawa pulang.</p>
              </li>
              <li className="notes-item">
                <span className="notes-num">05</span>
                <p className="notes-text">Pengambilan karya maksimal 2 bulan setelah kelas berlangsung. Lewat dari batas waktu tersebut, karya bukan lagi menjadi tanggung jawab kami.</p>
              </li>
              <li className="notes-item">
                <span className="notes-num">06</span>
                <p className="notes-text">Setelah pembayaran berhasil, invoice akan dikirimkan secara otomatis ke nomor WhatsApp Anda. Mohon pastikan Anda memasukkan nomor WhatsApp yang aktif saat mengisi form booking.</p>
              </li>
            </ol>
          </div>
        )}
      </>
    );
  };

  const fmtPrice = (p) => {
    if (!p) return "—";
    if (p >= 1000000) return `Rp ${(p / 1000000).toFixed(1).replace('.0', '')} Juta`;
    if (p >= 1000) return `Rp ${(p / 1000).toLocaleString('id-ID')}K`;
    return `Rp ${p.toLocaleString('id-ID')}`;
  };

  useEffect(() => {
    // Scroll animations
    const animateElements = document.querySelectorAll('.animate-on-scroll, .animate-left, .animate-right, .animate-up, .animate-scale');

    const scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
          scrollObserver.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.1
    });

    animateElements.forEach(element => {
      scrollObserver.observe(element);
    });

    // Image lazy loading
    const images = document.querySelectorAll('img');
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          setTimeout(() => {
            if (img.complete && img.naturalHeight !== 0) {
              img.classList.add('loaded');
            } else {
              img.addEventListener('load', () => {
                img.classList.add('loaded');
              }, { once: true });
            }
          }, index * 100);
          imageObserver.unobserve(img);
        }
      });
    }, {
      rootMargin: '150px',
      threshold: 0.1
    });

    images.forEach(img => {
      if (img.loading === 'lazy') {
        imageObserver.observe(img);
      }
    });

    return () => {
      scrollObserver.disconnect();
      imageObserver.disconnect();
    };
  }, [loading, experiences]);

  return (
    <div className="reservation-page-container">
      {/* Section 1 - Hero */}
      <Hero
        useSlideshow={true}
        images={[
          '/images/section6ahome_pottery1.webp',
          '/images/section7ahome_painting2.webp',
          '/images/sailing/sailing1.webp'
        ]}
      />

      {/* Section 2 - Experiences Introduction */}
      <section className="reservation-intro-section animate-on-scroll">
        <div className="intro-ornament top">◆</div>
        <div className="reservation-intro-content">
          <h2 className="intro-title animate-up">TEMUKAN KETENANGAN ANDA</h2>
          <div className="intro-divider"></div>
          <p className="intro-description animate-up">
            Selain pengalaman bersantap yang luar biasa, kami menawarkan pengalaman imersif yang menghubungkan Anda kembali dengan alam,
            kreativitas, dan seni hidup lambat (slow living). Setiap petualangan dirancang dengan cermat untuk menciptakan
            kenangan abadi di tepi Danau Tondano.
          </p>
        </div>
        <div className="intro-ornament bottom">◆</div>
      </section>

      {/* Section 3 - Bicycle Rental */}
      <section className="experience-section bicycle-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-right">
            <div className="experience-header">
              <h3 className="experience-title">BICYCLE RENTAL</h3>
            </div>

            <div className="experience-image-wrapper animate-left">
              <div className="image-frame premium">
                <img
                  src="/images/reservationsection3_bicycling1.webp"
                  alt="Bicycle rental experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Petualangan Outdoor</span>
              </div>
            </div>

            <p className="experience-description">
              Jelajahi jalur tepi danau yang indah dengan sepeda gunung dan road bike premium kami. Rasakan angin sepoi-sepoi saat Anda mengayuh melalui jalur yang tenang, menemukan sudut-sudut tersembunyi dari keindahan alam Danau Tondano.
            </p>

            <div className="res-package-card single-package" style={{ maxWidth: '600px', margin: '0 auto 30px' }}>
              <h4 className="res-package-title">Bicycle Rental</h4>
              <div className="res-package-price">Rp 175.000 <span>/ orang</span></div>
              <ul className="res-package-features">
                <li>Sepeda untuk trip</li>
                <li>Guide / pemandu</li>
                <li>Durasi ±1–2,5 jam (±12 km)</li>
                <li style={{ color: '#8B7355', fontWeight: 'bold' }}>+ Foto & Video Drone: Rp 300.000 (Opsional)</li>
              </ul>
              <button
                className={`experience-btn primary ${checkAvailability(getExp('bicycle')).disabled ? 'disabled' : ''}`}
                disabled={checkAvailability(getExp('bicycle')).disabled}
                onClick={() =>
                  handleBook({
                    id: "bicycle",
                    name: "Bicycle Rental",
                    price: 175000,
                    icon: "🚴",
                    notes: [
                      "Booking dianggap terkonfirmasi setelah melakukan pembayaran.",
                      "Pembatalan booking setelah pembayaran dilakukan tidak dapat di-refund dalam kondisi apapun.",
                      "Reschedule maksimal H-1 sebelum jadwal booking dan mohon menghubungi admin di nomor 0821-8761-1080."
                    ]
                  })
                }
              >
                <span>{checkAvailability(getExp('bicycle')).text === "Sedang Tidak Tersedia" ? "Sedang Tidak Tersedia" : "PESAN SEKARANG"}</span>
                <span className="btn-arrow">→</span>
              </button>
            </div>

            <div className="experience-pottery-notes">
            <h5 className="notes-title">Catatan Rental Sepeda</h5>
            <ol className="notes-list">
                <li className="notes-item">
                  <span className="notes-num">01</span>
                  <p className="notes-text">Booking dianggap terkonfirmasi setelah melakukan pembayaran.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">02</span>
                  <p className="notes-text">Pembatalan booking setelah pembayaran dilakukan tidak dapat di-refund dalam kondisi apapun.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">03</span>
                  <p className="notes-text">Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.</p>
                </li>
            </ol>
          </div>

            <div className="activity__best-time" style={{ color: '#8B7355', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              <span className="material-symbols-outlined">schedule</span>
              Waktu terbaik: Pagi 05.30 · Sore 16.00
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Paint on Pottery (Earthenware) */}
      <section className="experience-section painting-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-left">
            <div className="experience-header">
              <h3 className="experience-title">{getExp('paint-on-pottery')?.name?.toUpperCase() || "PAINT ON POTTERY (EARTHENWARE)"}</h3>
            </div>

            <div className="experience-image-wrapper animate-right">
              <div className="image-frame premium">
                <img
                  src="/images/section7ahome_painting2.webp"
                  alt="Paint on Pottery experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Workshop Kreatif</span>
              </div>
            </div>

            {renderDynamicContent(
              'paint-on-pottery',
              'Ekspresikan kreativitas Anda dengan melukis di atas tembikar tanah liat (earthenware). Dengan peralatan lengkap kami, ubah tembikar polos menjadi karya seni pribadi Anda.',
              ['Media tembikar', 'Peralatan lengkap', 'Hasil karya dapat dibawa pulang', 'Durasi sesi 2 jam'],
              200000
            )}
            <button
              className={`experience-btn primary ${checkAvailability(getExp('paint-on-pottery')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('paint-on-pottery')).disabled}
              onClick={() =>
                handleBook({
                  id: "paint-on-pottery",
                  name: getExp('paint-on-pottery')?.name || "Paint on Pottery (Earthenware)",
                  price: getExp('paint-on-pottery')?.price || 200000,
                  icon: getExp('paint-on-pottery')?.emoji || "🎨"
                })
              }
            >
              <span>
                {checkAvailability(getExp('paint-on-pottery')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 5 - Pottery (Earthenware) */}
      <section className="experience-section pottery-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-right">
            <div className="experience-header">
              <h3 className="experience-title">{getExp('pottery')?.name?.toUpperCase() || "POTTERY CLASS (EARTHENWARE)"}</h3>
            </div>

            <div className="experience-image-wrapper animate-left">
              <div className="image-frame premium">
                <img
                  src="/images/section6ahome_pottery2.webp"
                  alt="Pottery Earthenware experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Workshop Kreatif</span>
              </div>
            </div>

            {renderDynamicContent(
              'pottery',
              'Rasakan pengalaman seni tembikar kuno. Bentuk 1 kg tanah liat earthenware menjadi karya unik di bawah bimbingan instruktur berpengalaman. Sesi berlangsung selama 2 jam.',
              ['1 kg tanah liat', 'Peralatan lengkap', 'Pendampingan instruktur', 'Hasil karya dapat dibawa pulang', 'Durasi sesi 2 jam'],
              200000
            )}
            <button
              className={`experience-btn primary ${checkAvailability(getExp('pottery')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('pottery')).disabled}
              onClick={() =>
                handleBook({
                  id: "pottery",
                  name: getExp('pottery')?.name || "Pottery Class (Earthenware)",
                  price: getExp('pottery')?.price || 200000,
                  icon: getExp('pottery')?.emoji || "🏺"
                })
              }
            >
              <span>
                {checkAvailability(getExp('pottery')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 6 - Pottery (Stoneware) */}
      <section className="experience-section pottery-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-left">
            <div className="experience-header">
              <h3 className="experience-title">{getExp('pottery-stoneware')?.name?.toUpperCase() || "POTTERY CLASS (STONEWARE)"}</h3>
            </div>

            <div className="experience-image-wrapper animate-right">
              <div className="image-frame premium">
                <img
                  src="/images/pottery/stoneware1.webp"
                  alt="Pottery Stoneware experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Workshop Premium</span>
              </div>
            </div>

            {renderDynamicContent(
              'pottery-stoneware',
              'Buat tembikar stoneware premium dengan hasil akhir glasir transparan profesional. Stoneware berkualitas tinggi yang aman untuk makanan (food-grade).',
              ['700 gram tanah liat premium', 'Peralatan lengkap', 'Pendampingan instruktur', 'Glasir transparan', 'Food grade', 'Hasil karya dapat dibawa pulang', 'Durasi sesi 2 jam'],
              300000
            )}
            <button
              className={`experience-btn primary ${checkAvailability(getExp('pottery-stoneware')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('pottery-stoneware')).disabled}
              onClick={() =>
                handleBook({
                  id: "pottery-stoneware",
                  name: getExp('pottery-stoneware')?.name || "Pottery Class (Stoneware)",
                  price: getExp('pottery-stoneware')?.price || 300000,
                  icon: getExp('pottery-stoneware')?.emoji || "🏺"
                })
              }
            >
              <span>
                {checkAvailability(getExp('pottery-stoneware')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 7 - Pottery & Painting (Earthenware) */}
      <section className="experience-section combo-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-right">
            <div className="experience-header">
              <h3 className="experience-title">{getExp('combo')?.name?.toUpperCase() || "POTTERY PAINTING & POTTERY MAKING (EARTHENWARE)"}</h3>
            </div>

            <div className="experience-image-wrapper animate-left">
              <div className="image-frame premium">
                <img
                  src="/images/pottery/potterypainting.webp"
                  alt="Pottery & Painting (Earthenware) experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Kombo Kreatif</span>
              </div>
            </div>

            {renderDynamicContent(
              'combo',
              'Pengalaman kreatif terbaik — bentuk tanah liat menjadi tembikar dan lukis karya Anda dalam satu sesi yang dipandu oleh instruktur profesional.',
              ['1 kg tanah liat', '1 media terracotta', 'Peralatan lengkap', 'Pendampingan instruktur', 'Hasil karya dapat dibawa pulang', 'Durasi sesi 3 jam'],
              350000
            )}
            <button
              className={`experience-btn primary ${checkAvailability(getExp('combo')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('combo')).disabled}
              onClick={() =>
                handleBook({
                  id: "combo",
                  name: getExp('combo')?.name || "Pottery Painting & Pottery Making (Earthenware)",
                  price: getExp('combo')?.price || 350000,
                  icon: getExp('combo')?.emoji || "🎭"
                })
              }
            >
              <span>
                {checkAvailability(getExp('combo')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 8 - Paint & Pottery (Stoneware) */}
      <section className="experience-section combo-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-left">
            <div className="experience-header">
              <h3 className="experience-title">{getExp('combo-stoneware')?.name?.toUpperCase() || "PAINT & POTTERY (STONEWARE)"}</h3>
            </div>

            <div className="experience-image-wrapper animate-right">
              <div className="image-frame premium">
                <img
                  src="/images/pottery/potterypainting2.webp"
                  alt="Paint & Pottery (Stoneware) experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Kombo Premium</span>
              </div>
            </div>

            {renderDynamicContent(
              'combo-stoneware',
              'Kombo stoneware premium — buat tembikar dan lukis dengan teknik underglaze, diakhiri dengan glasir transparan. *Sesi melukis dilakukan 2 hari setelah sesi tembikar.',
              ['700 gram tanah liat', 'Peralatan lengkap', 'Pendampingan instruktur', 'Glasir transparan', 'Food grade', 'Hasil karya dapat dibawa pulang', 'Pewarnaan menggunakan underglaze', 'Durasi sesi: 3 jam'],
              500000
            )}
            <button
              className={`experience-btn primary ${checkAvailability(getExp('combo-stoneware')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('combo-stoneware')).disabled}
              onClick={() =>
                handleBook({
                  id: "combo-stoneware",
                  name: getExp('combo-stoneware')?.name || "Paint & Pottery (Stoneware)",
                  price: getExp('combo-stoneware')?.price || 500000,
                  icon: getExp('combo-stoneware')?.emoji || "🎭"
                })
              }
            >
              <span>
                {checkAvailability(getExp('combo-stoneware')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 8.5 - Pottery (Stoneware) & Painting (Earthenware) */}
      <section className="experience-section combo-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-right">
            <div className="experience-header">
              <h3 className="experience-title">{getExp('pottery-stoneware-painting-earthenware')?.name?.toUpperCase() || "POTTERY (STONEWARE) & PAINTING (EARTHENWARE)"}</h3>
            </div>

            <div className="experience-image-wrapper animate-left">
              <div className="image-frame premium">
                <img
                  src="/images/pottery/potterypainting3.webp"
                  alt="Pottery (Stoneware) & Painting (Earthenware) experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Kombo Kreatif</span>
              </div>
            </div>

            {renderDynamicContent(
              'pottery-stoneware-painting-earthenware',
              'Gabungkan dua pengalaman terbaik: buat tembikar stoneware dan lukis media terracotta earthenware dalam satu sesi.',
              ['700 gram tanah liat', '1 media terracotta untuk melukis', 'Peralatan lengkap', 'Pendampingan instruktur', 'Hasil karya dapat dibawa pulang', 'Glasir transparan untuk tembikar stoneware', 'Durasi sesi: 3 jam'],
              450000
            )}
            <button
              className={`experience-btn primary ${checkAvailability(getExp('pottery-stoneware-painting-earthenware')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('pottery-stoneware-painting-earthenware')).disabled}
              onClick={() =>
                handleBook({
                  id: "pottery-stoneware-painting-earthenware",
                  name: getExp('pottery-stoneware-painting-earthenware')?.name || "Pottery (Stoneware) & Painting (Earthenware)",
                  price: getExp('pottery-stoneware-painting-earthenware')?.price || 450000,
                  icon: getExp('pottery-stoneware-painting-earthenware')?.emoji || "🏺🎨"
                })
              }
            >
              <span>
                {checkAvailability(getExp('pottery-stoneware-painting-earthenware')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 9 - Sailing Experience */}
      <section className="experience-section sailing-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-right">
            <div className="experience-header">
              <h3 className="experience-title">SAILING TRIP</h3>
            </div>

            <div className="experience-image-wrapper animate-left">
              <div className="image-frame premium">
                <img
                  src="/images/sailing/sailing1.webp"
                  alt="Sailing experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Petualangan Air</span>
              </div>
            </div>

            {renderDynamicContent(
              'sailing',
              'Berkeliling Danau Tondano dengan suasana tenang dan pemandangan indah. Trip berdurasi 20–30 menit.',
              ['Durasi 20–30 menit', 'Berkeliling Danau Tondano', 'Life jacket dan perlengkapan keselamatan'],
              50000,
              '20–30 MENIT'
            )}

            <div className="experience-pottery-notes">
            <h5 className="notes-title">Catatan Sailing Trip</h5>
            <ol className="notes-list">
                <li className="notes-item">
                  <span className="notes-num">01</span>
                  <p className="notes-text">Harga minimal Rp 250.000 (untuk 1–5 orang).</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">02</span>
                  <p className="notes-text">Jika lebih dari 5 orang, dikenakan biaya tambahan Rp 50.000 per orang.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">03</span>
                  <p className="notes-text">Booking dianggap terkonfirmasi setelah melakukan pembayaran.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">04</span>
                  <p className="notes-text">Life jacket dan perlengkapan keselamatan wajib digunakan.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">05</span>
                  <p className="notes-text">Pembatalan booking setelah pembayaran dilakukan tidak dapat dikembalikan (non-refundable) dalam kondisi apapun.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">06</span>
                  <p className="notes-text">Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">07</span>
                  <p className="notes-text">Mohon datang tepat waktu sesuai jadwal booking. Keterlambatan lebih dari 10 menit akan menyebabkan booking hangus.</p>
                </li>
            </ol>
          </div>

            <button
              className={`experience-btn primary ${checkAvailability(getExp('sailing')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('sailing')).disabled}
              onClick={() =>
                handleBook({
                  id: "sailing",
                  name: getExp('sailing')?.name || "Sailing Trip",
                  price: getExp('sailing')?.price || 50000,
                  icon: getExp('sailing')?.emoji || "⛵",
                  notes: [
                    "Harga minimal Rp 250.000 (untuk 1–5 orang).",
                    "Jika lebih dari 5 orang, dikenakan biaya tambahan Rp 50.000 per orang.",
                    "Maksimal 15 orang per trip.",
                    "Durasi 20–30 menit berkeliling Danau Tondano.",
                    "Booking terkonfirmasi setelah pembayaran.",
                    "Life jacket dan perlengkapan keselamatan wajib.",
                    "Pembatalan non-refundable.",
                    "Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.",
                    "Keterlambatan > 10 menit menyebabkan booking hangus."
                  ]
                })
              }
            >
              <span>
                {checkAvailability(getExp('sailing')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 10 - Kayak Experience */}
      <section className="experience-section kayak-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-left">
            <div className="experience-header">
              <h3 className="experience-title">KAYAK / CANOEING</h3>
            </div>

            <div className="experience-image-wrapper animate-right">
              <div className="image-frame premium">
                <img
                  src="/images/kayak/kayak1.webp"
                  alt="Kayak experience"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Petualangan Air</span>
              </div>
            </div>

            {renderDynamicContent(
              'kayak',
              'Jelajahi ketenangan Danau Tondano dengan ritme Anda sendiri. Perpaduan sempurna antara olahraga ringan dan koneksi mendalam dengan alam.',
              ['Durasi 30 menit', 'Life jacket dan perlengkapan keselamatan', 'Menjelajahi danau dengan santai sesuai keinginan', 'Maksimal 2 orang per kayak'],
              275000,
              '30 MENIT'
            )}

            <div className="experience-pottery-notes">
            <h5 className="notes-title">Catatan Kayak</h5>
            <ol className="notes-list">
                <li className="notes-item">
                  <span className="notes-num">01</span>
                  <p className="notes-text">Booking dianggap terkonfirmasi setelah melakukan pembayaran.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">02</span>
                  <p className="notes-text">Pembatalan booking setelah pembayaran dilakukan tidak dapat dikembalikan (non-refundable) dalam kondisi apapun.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">03</span>
                  <p className="notes-text">Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">04</span>
                  <p className="notes-text">Mohon datang tepat waktu sesuai jadwal booking. Keterlambatan lebih dari 10 menit akan menyebabkan booking hangus.</p>
                </li>
            </ol>
          </div>

            <button
              className={`experience-btn primary ${checkAvailability(getExp('kayak')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('kayak')).disabled}
              onClick={() =>
                handleBook({
                  id: "kayak",
                  name: getExp('kayak')?.name || "Kayak / Canoeing",
                  price: getExp('kayak')?.price || 275000,
                  icon: "🛶",
                  notes: [
                    "Maksimal 2 orang per kayak.",
                    "Durasi 30 menit.",
                    "Booking terkonfirmasi setelah pembayaran.",
                    "Pembatalan non-refundable.",
                    "Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.",
                    "Keterlambatan > 10 menit menyebabkan booking hangus."
                  ]
                })
              }
            >
              <span>
                {checkAvailability(getExp('kayak')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Section 11 - Pre-Wedding Package */}
      <section className="experience-section prewedding-section animate-on-scroll">
        <div className="experience-container">
          <div className="experience-content animate-left">
            <div className="experience-header">
              <h3 className="experience-title">PHOTOSHOOT PACKAGE</h3>
            </div>

            <div className="experience-image-wrapper animate-right">
              <div className="image-frame premium">
                <img
                  className="prewedding-main-image"
                  src="/images/prewed/prewed2.jpg"
                  alt="Pre-Wedding Package"
                  loading="lazy"
                  onLoad={(e) => e.target.classList.add('loaded')}
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="experience-badge">
                <span className="badge-text">Pengalaman Eksklusif</span>
              </div>
            </div>

            {renderDynamicContent(
              'prewedding',
              'Abadikan momen spesial Anda dengan akses penuh ke perahu, area tembikar, dan berbagai sudut estetik restoran.',
              ['Akses perahu kecil', 'Akses perahu besar', 'Sesi tembikar & melukis', 'Area photoshoot di restoran', 'Durasi 3 jam (1 jam persiapan & 2 jam photoshoot)'],
              1500000,
              '3 JAM'
            )}

            <div className="experience-pottery-notes">
            <h5 className="notes-title">Catatan Photoshoot</h5>
            <ol className="notes-list">
                <li className="notes-item">
                  <span className="notes-num">01</span>
                  <p className="notes-text">Durasi penggunaan dihitung sesuai jam booking yang dipilih (contoh: pukul 09.00–12.00). Apabila melebihi waktu yang ditentukan, akan dikenakan biaya tambahan sebesar Rp500.000 per jam.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">02</span>
                  <p className="notes-text">Booking dianggap terkonfirmasi setelah melakukan pembayaran.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">03</span>
                  <p className="notes-text">Pembatalan booking setelah pembayaran dilakukan tidak dapat dikembalikan (non-refundable) dalam kondisi apapun.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">04</span>
                  <p className="notes-text">Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.</p>
                </li>
                <li className="notes-item">
                  <span className="notes-num">05</span>
                  <p className="notes-text">Mohon datang tepat waktu sesuai jadwal booking. Keterlambatan lebih dari 10 menit akan menyebabkan booking hangus.</p>
                </li>
            </ol>
          </div>

            <button
              className={`experience-btn primary ${checkAvailability(getExp('prewedding')).disabled ? 'disabled' : ''}`}
              disabled={checkAvailability(getExp('prewedding')).disabled}
              onClick={() =>
                handleBook({
                  id: "prewedding",
                  name: getExp('prewedding')?.name || "Photoshoot Package",
                  price: getExp('prewedding')?.price || 1500000,
                  icon: getExp('prewedding')?.emoji || "📸",
                  duration: "3 hours (1 hour prep & 2 hours photoshoot)",
                  notes: [
                    "Durasi penggunaan dihitung sesuai jam booking yang dipilih (contoh: pukul 09.00–12.00). Apabila melebihi waktu yang ditentukan, akan dikenakan biaya tambahan sebesar Rp500.000 per jam.",
                    "Booking dianggap terkonfirmasi setelah melakukan pembayaran.",
                    "Pembatalan booking setelah pembayaran dilakukan tidak dapat dikembalikan (non-refundable) dalam kondisi apapun.",
                    "Apabila ingin melakukan reschedule, mohon menghubungi admin maksimal H-1 sebelum jadwal aktivitas.",
                    "Mohon datang tepat waktu sesuai jadwal booking. Keterlambatan lebih dari 10 menit akan menyebabkan booking hangus."
                  ]
                })
              }
            >
              <span>
                {checkAvailability(getExp('prewedding')).text}
              </span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Reservation;
